import React from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { BreadcrumbItemType, BreadcrumbSeparatorType } from 'antd/es/breadcrumb/Breadcrumb';

import { ItemProvider, useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { List } from '@common/typescript/objects/List';

import { rest } from '@app/components/Api';
import CustomRequestProvider from '@app/components/CustomRequestProvider/CustomRequestProvider';
import LinkMore from '@app/components/LocalCommon/LinkMore/LinkMore';
import CustomMarkup from '@app/components/UI/CustomMarkup/CustomMarkup';
import HelmetProvider from '@app/components/UI/HelmetProvider/HelmetProvider';
import BlockRich from '@app/components/UI/LinkBlock/BlockRich';
import Masthead from '@app/components/UI/MastHead/Masthead';
import { Resource } from '@app/objects/Resource';
import { ResourceTopic } from '@app/objects/ResourceTopic';
import '@app/scss/components/cardPhoto.scss';

interface ResourcePageProps {
	currentTopic?: ResourceTopic;
	topics?: Array<ResourceTopic>;
	itemTopic?: ResourceTopic;
}

const getBreadcrumbs = (
	item: Resource,
	currentTopic: ResourceTopic | undefined,
	itemTopic?: ResourceTopic,
): Array<Partial<BreadcrumbItemType & BreadcrumbSeparatorType>> | undefined => {
	return (
		item ? (
			[
				{
					path: '/resource-center',
					title: 'Resources',
				},
				{
					path: `/resource-center/${currentTopic?.url || itemTopic?.url || ''}`,
					title: currentTopic?.name || itemTopic?.name || '',
				},
				{
					path: '',
					title: item.name,
				},
			]
		) : undefined
	);
};

const ResourceComponent: React.FC<ResourcePageProps> = ({ currentTopic, itemTopic }) => {
	const context = useItemProviderContext<Resource>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { item },
	} = context;

	const [resources, setResources] = React.useState<Array<Resource>>();
	const [breadcrumbs, setBreadcrumbs] = React.useState<Array<Partial<BreadcrumbItemType & BreadcrumbSeparatorType>> | undefined>(getBreadcrumbs(item, currentTopic, itemTopic));
	const [imageTopAlts, setImageTopAlts] = React.useState({});
	const [imageAlts, setImageAlts] = React.useState({});
	const [imageBottomAlts, setImageBottomAlts] = React.useState({});

	React.useEffect(() => {
		if (item) {
			rest.get<List<Resource>>(`v1/resources/${item.id}/related`)
				.then((res) => setResources(res.list));
		}
	}, [item]);

	React.useEffect(() => {
		if (currentTopic && item) {
			setBreadcrumbs(getBreadcrumbs(item, currentTopic));

			const imageTA = {
				addisons: "Lethargic dog with Addison's disease",
				'brain-tumors': item.name.toLowerCase().includes('dogs') ? 'Dog with brain tumor resting on couch' : 'Cat with brain tumor',
				'cognitive-dysfunction': 'Cognitive Dysfunction dog with increased activity',
				diabetes: item.name.toLowerCase().includes('dogs') && 'Diabetic dog with increased appetite',
				'elbow-and-hip-dysplasia': 'Dog with hip dysplasia reluctant to get up',
				hemangiosarcoma: 'Decreased appetite in dog with hemangiosarcoma',
				ibd: item.name.toLowerCase().includes('dogs') ? 'IBD in Dogs' : 'IBD in Cats',
				imha: 'IMHA in Dogs',
				ivdd: 'IVDD in Dogs',
				larpar: 'Excessive panting in dog with laryngeal paralysis',
				'non-cancerous-masses-and-tumors': item.name.toLowerCase().includes('dogs')
					? 'Lethargic dog with non-cancerous tumor'
					: 'Non-Cancerous Masses and Tumors in Cats',
				tcc: item.name.toLowerCase().includes('dogs') ? 'TCC in Dogs' : 'TCC in Cats',
				flutd: 'FLUTD in Cats',
				lymphoma: item.name.toLowerCase().includes('cats') && 'Lethargy in cat with lymphoma',
				'vestibular-syndrome': item.url.includes('cats') && 'Vestibular Syndrome in Cats',
				'behavior-changes': 'Behavior Changes & Aggression in Cats',
				'breathing-problems': 'Breathing Problems in Dogs',
				coughing: 'Coughing in Dogs',
				'decreased-appetite': 'Decreased Appetite in Dogs',
				dementia: 'Dementia in Dogs',
				diarrhea: 'Diarrhea in Cats',
				'geriatric-veterinary-patients': 'Senior pet veterinary care',
				'lameness-limping': 'Lameness & Limping in Dogs ',
				'mobility-disorders': 'Mobility Disorders in Cats',
				'paralysis-or-paresis': 'Paralysis or Paresis in Dogs',
				regurgitation: 'Regurgitation in Cats ',
				restlessness: 'Restlessness in Cats',
				seizures: item.name.toLowerCase().includes('dogs')
					? 'Seizures in Dogs'
					: item.name.toLowerCase().includes('cats')
						? 'Seizures in Cats'
						: 'Seizures in Dogs',
				shaking: 'Shaking in Cats',
				'urinary-incontinence': 'Urinary Incontinence in Cats',
				'vision-problems-and-blindness': 'Vision Problems & Blindness in Dogs',
				vomiting: 'Vomiting in Dogs ',
				'weight-loss': 'Weight Loss in Dogs ',
			};

			setImageTopAlts(imageTA);

			const imageA = {
				'geriatric-veterinary-patients': {
					'urinary-incontinence': 'Pet urinary incontinence ',
					'behavior-changes': 'Senior cat',
					'vision-problems-and-blindness': 'Dog disease',
				},
			};

			setImageAlts(imageA);

			const imageBA = {
				addisons: "Dog with decreased energy suffering from Addison's disease",
				'chronic-kidney-disease': item.name.toLowerCase().includes('dogs') ? 'Dog with incresed thirst' : 'Cat with incresed thirst',
				'cognitive-dysfunction': 'Disoriented dog suffering from Congnitive Dysfunction',
				'degenerative-myelopathy': 'Hind limb weakness in dog with Degenerative Myelopathy',
				'heart-disease': item.name.toLowerCase().includes('dogs') ? 'Doberman with heart disease' : 'Cat with heart disease',
				hyperadrenocorticism: "Panting dog wtih Cushing's disease",
				ibd: item.name.toLowerCase().includes('dogs')
					? 'Inflammatory bowel disease (IBD) in dogs'
					: 'Inflammatory bowel disease (IBD) in cats',
				imha: 'immune-mediated hemolytic anemia (IMHA) in dogs',
				larpar: 'LARPAR in Dogs',
				'liver-disease': item.name.toLowerCase().includes('dogs')
					? 'Dog with liver disease looking out the window'
					: 'Cat with liver disease lying on couch',
				'lung-tumors': item.name.toLowerCase().includes('dogs')
					? 'Labored breathing in dog with lung tumor'
					: 'Lethargy in cat with lung tumor',
				lymphoma: item.name.toLowerCase().includes('dogs') && 'Swollen face in dog with lymphoma',
				'mast-cell-tumors': item.name.toLowerCase().includes('dogs')
					? 'Distended abdomen in dog with mast cell tumors'
					: 'Cat with mast cell tumor (MCT)',
				'nasal-tumors': item.name.toLowerCase().includes('dogs')
					? 'Nasal discharge in dog with nasal tumor'
					: 'Cat suffering from nasal tumor',
				'oral-tumors': item.name.toLowerCase().includes('dogs')
					? 'Facial swelling in dog with oral tumor'
					: 'Facial swelling in cat with oral tumor',
				osteoarthritis: item.name.toLowerCase().includes('dogs')
					? 'Lameness in dog with osteoarthritis'
					: 'Irritability in cat with osteoarthritis',
				osteosarcoma: 'Leg swelling in dog with osteosarcoma',
				seizures: item.name.toLowerCase().includes('dogs')
					? 'Dog with seizure'
					: item.name.toLowerCase().includes('cats')
						? 'Cat fallen over having a seizure'
						: 'Seizures in Cats',
				'vestibular-syndrome': item.name.toLowerCase().includes('dogs')
					? 'Inability to stand up in dog with vestibular syndrome'
					: 'Inability to stand up in cat with vestibular syndrome',
				'brain-tumors': 'Brain Tumors in Cats',
				diabetes: item.name.toLowerCase().includes('dogs') ? 'Diabetes Mellitus in Dogs' : 'Diabetic cat with decreased activity',
				'hepatic-lipidosis': 'Fatty liver disease in cats',
				hyperthyroidism: 'Weight loss in cat with hyperthyroidism',
				'non-cancerous-masses-and-tumors': item.name.toLowerCase().includes('cats') && 'Lethargic cat with non-cancerous tumor',
				tcc: item.name.toLowerCase().includes('cats') && 'Transitional Cell Carcinoma in Cats',
				'behavior-changes': 'Behavior Changes & Aggression in Dogs',
				'breathing-problems': 'Breathing Problems in Cats',
				coughing: 'Coughing in Cats',
				'decreased-appetite': 'Decreased Appetite in Cats',
				dementia: 'Dementia in Cats',
				diarrhea: 'Diarrhea in Dogs',
				'lameness-limping': 'Lameness & Limping in Cats',
				'mobility-disorders': 'Mobility Disorders in Dogs',
				'paralysis-or-paresis': 'Paralysis or Paresis in Cats',
				regurgitation: 'Regurgitation in Dogs',
				restlessness: 'Restlessness in Dogs',
				shaking: 'Shaking in Dogs',
				'urinary-incontinence': 'Urinary Incontinence in Dogs',
				'vision-problems-and-blindness': 'Vision Problems & Blindness in Cats',
				vomiting: 'Vomiting in Cats',
				'weight-loss': 'Weight Loss in Cats ',
			};

			setImageBottomAlts(imageBA);
		}
	}, [item, currentTopic]);

	return item ? (
		<>
			<Helmet>
				<title>{`${item.title} | Lap of Love`}</title>
				<meta property="og:title" content={`${item.title} | Lap of Love`} />
				<meta name="twitter:title" content={`${item.title} | Lap of Love`} />
				<meta name="description" content={item.description || undefined} />
				<meta property="og:description" content={item.description || undefined} />
				<meta name="twitter:description" content={item.description || undefined} />
			</Helmet>

			{breadcrumbs && (
				<div>
					<Masthead
						title={item?.title}
						imageSrc={item.avatar}
						breadcrumbs={breadcrumbs}
						backPath={{
							path: `/resource-center/${currentTopic?.url || ''}`,
							name: 'Back to resource center',
						}}
						imageAlt={imageTopAlts[item.url] ? imageTopAlts[item.url] : item.name}
					/>
				</div>
			)}

			<CustomMarkup text={item.fullText} alt={imageBottomAlts[item.url] ? imageBottomAlts[item.url] : item.name} />

			{resources && resources.length === 3 && (
				<BlockRich
					subtitle="Additional resources"
					title={
						<>
							You may also find these resources
							{' '}
							<span className="highlight">helpful</span>
						</>
					}
					text={<LinkMore linkMoreLink={`/resource-center/${currentTopic?.url}`} linkMoreCaption="Explore all of our resources" />}
					blockClassName="link-block-rich_modern"
					images={[
						{
							title: (
								<>
									<span>{resources[0]?.topic?.name}</span>
									<br />
									<span className="h4">{resources[0].title}</span>
								</>
							),
							src: resources[0].avatar,
							link: `/resource-center/${(resources[0].topic || currentTopic)?.url}/${resources[0].url}`,
							alt: item && imageAlts[item.url] && imageAlts[item.url][resources[0].url],
						},
						{
							title: (
								<>
									<span>{resources[1].topic?.name}</span>
									<br />
									<span className="h4">{resources[1].title}</span>
								</>
							),
							src: resources[1].avatar,
							link: `/resource-center/${(resources[1].topic || currentTopic)?.url}/${resources[1].url}`,
							alt: item && imageAlts[item.url] && imageAlts[item.url][resources[1].url],
						},
						{
							title: (
								<>
									<span>{resources[2].topic?.name}</span>
									<br />
									<span className="h4">{resources[2].title}</span>
								</>
							),
							src: resources[2].avatar,
							link: `/resource-center/${(resources[2].topic || currentTopic)?.url}/${resources[2].url}`,
							alt: item && imageAlts[item.url] && imageAlts[item.url][resources[2].url],
						},
					]}
				/>
			)}
		</>
	) : (
		<div className="h100">
			<BounceLoader />
		</div>
	);
};

const ResourcePage: React.FC<ResourcePageProps> = ({ currentTopic, topics }) => {
	const params = useParams<{ topic, resource }>();

	return (
		<HelmetProvider>
			{(page) => (
				<CustomRequestProvider>
					<ItemProvider<Resource>
						type={currentTopic ? `resourceTopics/${currentTopic.id}/resources/${params.resource}` : ''}
						loadRequest={currentTopic ? `resourceTopics/${currentTopic.id}/resources/${params.resource}` : ''}
						id={0}
						skipInitLoad={Boolean(page?.resource)}
						item={page?.resource}
					>
						<ResourceComponent currentTopic={currentTopic} itemTopic={topics?.find((topic) => topic.url === params.topic)} />
					</ItemProvider>
				</CustomRequestProvider>
			)}
		</HelmetProvider>
	);
};

export default ResourcePage;
